import { generateClient, GraphQLResult } from "aws-amplify/data";
import {
  processRetryExceededPaymentV2Mutation,
  getExceededPaymentV2Transactions,
  ITransaction,
  IGetPaymentV2FailedTransactionResult,
  IProcessRetryExceededPaymentV2MutationResponse,
  mutationSingleTransaction,
  mutationSingleTransactionRemove,
  IProcessSingleTransactionResult,
  IProcessSingleTransactionRemoveResult,
  IProcessResult,
} from "../lib";

interface IGetResponse {
  success: boolean;
  transactions?: ITransaction[];
  errorMsg?: string;
  totalFailedCount?: number;
  totalQueueCount?: number;
  totalLess12QueueCount?: number;
  totalLess24QueueCount?: number;
  totalLess48QueueCount?: number;
  totalMore48QueueCount?: number;
}

export const getPaymentV2FailedTransaction = async (
  page: number
): Promise<IGetResponse> => {
  try {
    const client = generateClient({
      authMode: "identityPool",
    });
    const res = (await client.graphql({
      query: getExceededPaymentV2Transactions,
      variables: {
        page,
      },
    })) as GraphQLResult<IGetPaymentV2FailedTransactionResult>;

    if (!res?.data?.getExceededPaymentV2Transactions) {
      throw new Error(
        "Invalid response while getting exceeded PaymentV2 transaction"
      );
    }
    const {
      transactions,
      totalFailedCount,
      totalQueueCount,
      totalLess12QueueCount,
      totalLess24QueueCount,
      totalLess48QueueCount,
      totalMore48QueueCount,
    } = res.data.getExceededPaymentV2Transactions;
    return {
      success: true,
      transactions,
      totalFailedCount,
      totalQueueCount,
      totalLess12QueueCount,
      totalLess24QueueCount,
      totalLess48QueueCount,
      totalMore48QueueCount,
    };
  } catch (err: any) {
    console.log("Error while fetching exceeded PaymentV2 transaction", err);
    const message =
      err.errors && err.errors.length
        ? err.errors[0].message
        : "Error while fetching exceeded PaymentV2 transaction";
    return { success: false, errorMsg: message };
  }
};

export const processSingleTransaction = async (
  transactionId: string,
  action: string
): Promise<IProcessResult> => {
  try {
    if (action === "remove") {
      const client = generateClient({
        authMode: "identityPool",
      });
      const res = (await client.graphql({
        query: mutationSingleTransactionRemove,
        variables: {
          transactionId,
          action,
        },
      })) as GraphQLResult<IProcessSingleTransactionRemoveResult>;

      if (!res?.data?.adminProcessSingleTransactionRemove) {
        throw new Error(
          `Invalid response while ${action} transaction ${transactionId}`
        );
      }
      return res.data.adminProcessSingleTransactionRemove;
    } else {
      const client = generateClient({
        authMode: "identityPool",
      });
      const res = (await client.graphql({
        query: mutationSingleTransaction,
        variables: {
          transactionId,
          action,
        },
      })) as GraphQLResult<IProcessSingleTransactionResult>;

      if (!res?.data?.adminProcessSingleTransaction) {
        throw new Error(
          `Invalid response while ${action} transaction ${transactionId}`
        );
      }

      return res.data.adminProcessSingleTransaction;
    }
  } catch (err: any) {
    console.log("Error while process single transaction", err);
    const message =
      err.errors && err.errors.length
        ? err.errors[0].message
        : `Error while ${action} transaction ${transactionId}`;
    return { success: false, errorMessage: message };
  }
};

export const rescheduleAllTransactions = async (): Promise<{
  success: boolean;
  count?: number;
  errorMsg?: string;
}> => {
  try {
    const client = generateClient({
      authMode: "identityPool",
    });
    const res = (await client.graphql({
      query: processRetryExceededPaymentV2Mutation,
    })) as GraphQLResult<IProcessRetryExceededPaymentV2MutationResponse>;

    console.log(`processRetryExceededPaymentV2`, res);

    const count = res.data?.processRetryExceededPaymentV2;
    if (count && count >= 0) {
      return { success: true, count };
    } else {
      throw new Error(JSON.stringify(res.data));
    }
  } catch (err: any) {
    console.log("Error while retrying PaymentV2 transactions", err);
    const message =
      err.errors && err.errors.length
        ? err.errors[0].message
        : "Error while retrying PaymentV2 transactions";
    return { success: false, errorMsg: message };
  }
};
